import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { PublicClientApplication, InteractionStatus } from '@azure/msal-browser';
import { userApi } from '../services/api';
import { useChatStore } from '../stores/chatStore';
import { setMsalInstance } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { instance, inProgress } = useMsal();
  const { setCurrentUser } = useChatStore();
  const [error, setError] = useState<string | null>(null);

  // Set MSAL instance for API calls
  setMsalInstance(instance as PublicClientApplication);

  useEffect(() => {
    const handleAuthRedirect = async () => {
      // Wait for MSAL to finish processing
      if (inProgress !== InteractionStatus.None) {
        return;
      }
      
      try {
        // Check all accounts MSAL has
        const allAccounts = instance.getAllAccounts();
        
        // Get or set active account
        let account = instance.getActiveAccount();
        
        // If no active account but we have accounts, set the first one as active
        if (!account && allAccounts.length > 0) {
          account = allAccounts[0];
          instance.setActiveAccount(account);
        }
        
        if (account) {
          try {
            // Try to get existing profile
            const user = await userApi.getProfile();
            setCurrentUser(user);
            navigate('/chat', { replace: true });
          } catch (err) {
            // Not linked yet - try to auto-link
            const userEmail = account.username || account.idTokenClaims?.email as string;
            
            if (userEmail) {
              try {
                // Use the display name from the External ID token
                const displayName = account.name || account.idTokenClaims?.name as string || 'User';
                
                const user = await userApi.linkAccount({
                  displayName,
                });
                
                setCurrentUser(user);
                navigate('/chat', { replace: true });
              } catch (linkErr: any) {
                console.error('Auto-link failed:', linkErr);
                setError('Unable to link your account. Please contact your administrator.');
              }
            } else {
              setError('Authentication completed but email not found.');
            }
          }
        } else {
          // No account found - redirect to join
          navigate('/join', { replace: true });
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        setError('Authentication failed. Please try again.');
      }
    };

    handleAuthRedirect();
  }, [instance, inProgress, navigate, setCurrentUser]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-wise-light to-white flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md text-center">
        <img src="/simplytechwise-logo.png" alt="Simply TechWise" className="h-16 mx-auto mb-4" />
        <h1 className="text-3xl font-bold text-wise-dark mb-4">
          WiseAssist
          <sub className="text-sm text-gray-500 ml-1">v{import.meta.env.VITE_APP_VERSION || '0.1.0'}</sub>
        </h1>
        
        {error ? (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
            <button
              onClick={() => navigate('/join', { replace: true })}
              className="w-full px-6 py-3 bg-wise text-white font-medium rounded-xl hover:bg-wise-dark transition-colors"
            >
              Back to Sign In
            </button>
          </div>
        ) : (
          <>
            <LoadingSpinner size="lg" />
            <p className="text-gray-600 mt-4">Completing sign in...</p>
          </>
        )}
      </div>
    </div>
  );
}
